import React, { useState, useEffect } from "react";

const WythoffGame = () => {
  const [pileA, setPileA] = useState(10);
  const [pileB, setPileB] = useState(20);
  const [customPileA, setCustomPileA] = useState(10); // 自定义A堆配置
  const [customPileB, setCustomPileB] = useState(20); // 自定义B堆配置
  const [currentPlayer, setCurrentPlayer] = useState(1); // 1 or 2
  const [gameMode, setGameMode] = useState("pvp"); // 'pvp', 'ai-easy', 'ai-hard'
  const [gameStatus, setGameStatus] = useState("playing"); // 'playing', 'finished'
  const [winner, setWinner] = useState(null);
  const [selectedStones, setSelectedStones] = useState([]); // [{pile: 'A'|'B', index: number}]
  const [aiMoveMessage, setAiMoveMessage] = useState("");
  const [flashingStones, setFlashingStones] = useState([]); // 电脑选择的石头闪烁
  
  // 新增状态
  const [showSettings, setShowSettings] = useState(false);
  const [playerFirst, setPlayerFirst] = useState(true); // 人机对战时玩家是否先手
  const [errorMessage, setErrorMessage] = useState(""); // 错误提示
  const [showError, setShowError] = useState(false);

  const PHI = (1 + Math.sqrt(5)) / 2; // 黄金比例

  // 显示错误提示
  const showErrorMessage = (message) => {
    setErrorMessage(message);
    setShowError(true);
    setTimeout(() => setShowError(false), 3000);
  };

  // 检查游戏是否结束
  useEffect(() => {
    if (pileA === 0 && pileB === 0) {
      setGameStatus("finished");
      setWinner(currentPlayer === 1 ? 2 : 1); // 上一个玩家获胜
    }
  }, [pileA, pileB, currentPlayer]);

  // 判断当前位置是否为必败态
  const isLosingPosition = (a, b) => {
    const min = Math.min(a, b);
    const max = Math.max(a, b);
    return Math.floor((max - min) * PHI) === min;
  };

  // 电脑AI移动
  const makeAIMove = () => {
    if (gameMode === "ai-easy") {
      makeRandomMove();
    } else if (gameMode === "ai-hard") {
      makeOptimalMove();
    }
  };

  // 随机移动
  const makeRandomMove = () => {
    const moves = [];

    // 从A堆取
    for (let i = 1; i <= pileA; i++) {
      moves.push({ type: "single", pile: "A", amount: i });
    }

    // 从B堆取
    for (let i = 1; i <= pileB; i++) {
      moves.push({ type: "single", pile: "B", amount: i });
    }

    // 同时从两堆取
    const maxBoth = Math.min(pileA, pileB);
    for (let i = 1; i <= maxBoth; i++) {
      moves.push({ type: "both", amount: i });
    }

    if (moves.length === 0) return;

    const randomMove = moves[Math.floor(Math.random() * moves.length)];
    makeAIMove_withAnimation(randomMove, "随机选择");
  };

  // 最优移动
  const makeOptimalMove = () => {
    // 如果当前是必败态，随机移动
    if (isLosingPosition(pileA, pileB)) {
      makeRandomMove();
      return;
    }

    // 寻找能到达必败态的移动
    const bestMove = findOptimalMove(pileA, pileB);
    if (bestMove) {
      makeAIMove_withAnimation(bestMove, "使用最优策略");
    } else {
      makeRandomMove();
    }
  };

  // 寻找最优移动
  const findOptimalMove = (a, b) => {
    // 尝试单堆移动
    for (let i = 1; i <= a; i++) {
      if (isLosingPosition(a - i, b)) {
        return { type: "single", pile: "A", amount: i };
      }
    }

    for (let i = 1; i <= b; i++) {
      if (isLosingPosition(a, b - i)) {
        return { type: "single", pile: "B", amount: i };
      }
    }

    // 尝试同时从两堆取
    const maxBoth = Math.min(a, b);
    for (let i = 1; i <= maxBoth; i++) {
      if (isLosingPosition(a - i, b - i)) {
        return { type: "both", amount: i };
      }
    }

    return null;
  };

  // 带动画的电脑移动
  const makeAIMove_withAnimation = (move, strategy) => {
    const flashStones = [];
    let message = "";

    if (move.type === "single") {
      if (move.pile === "A") {
        // 从A堆随机选择位置
        const availableIndexes = Array.from({ length: pileA }, (_, i) => i);
        const shuffled = availableIndexes.sort(() => Math.random() - 0.5);
        for (let i = 0; i < move.amount; i++) {
          flashStones.push({ pile: "A", index: shuffled[i] });
        }
        message = `电脑从A堆取了${move.amount}颗石头`;
      } else {
        // 从B堆随机选择位置
        const availableIndexes = Array.from({ length: pileB }, (_, i) => i);
        const shuffled = availableIndexes.sort(() => Math.random() - 0.5);
        for (let i = 0; i < move.amount; i++) {
          flashStones.push({ pile: "B", index: shuffled[i] });
        }
        message = `电脑从B堆取了${move.amount}颗石头`;
      }
    } else {
      // 从两堆随机选择位置
      const availableA = Array.from({ length: pileA }, (_, i) => i);
      const availableB = Array.from({ length: pileB }, (_, i) => i);
      const shuffledA = availableA.sort(() => Math.random() - 0.5);
      const shuffledB = availableB.sort(() => Math.random() - 0.5);
      for (let i = 0; i < move.amount; i++) {
        flashStones.push({ pile: "A", index: shuffledA[i] });
        flashStones.push({ pile: "B", index: shuffledB[i] });
      }
      message = `电脑从两堆各取了${move.amount}颗石头`;
    }

    setFlashingStones(flashStones);
    setAiMoveMessage(`${message} (${strategy})`);

    // 闪烁后执行移动
    setTimeout(() => {
      executeMove(move);
      setFlashingStones([]);
    }, 1800);
  };

  // 执行移动
  const executeMove = (move) => {
    if (move.type === "single") {
      if (move.pile === "A") {
        setPileA(Math.max(0, pileA - move.amount));
      } else {
        setPileB(Math.max(0, pileB - move.amount));
      }
    } else {
      setPileA(Math.max(0, pileA - move.amount));
      setPileB(Math.max(0, pileB - move.amount));
    }

    setCurrentPlayer(currentPlayer === 1 ? 2 : 1);
    setSelectedStones([]);
  };

  // 处理石头点击
  const handleStoneClick = (pile, index) => {
    if (gameStatus === "finished") return;
    if (gameMode !== "pvp" && currentPlayer === 2) return; // 电脑回合时不允许点击
    if (flashingStones.length > 0) return; // 电脑移动动画期间不允许点击

    const stoneKey = `${pile}-${index}`;
    const isSelected = selectedStones.some(
      (s) => `${s.pile}-${s.index}` === stoneKey
    );

    if (isSelected) {
      // 取消选择
      setSelectedStones(
        selectedStones.filter((s) => `${s.pile}-${s.index}` !== stoneKey)
      );
    } else {
      // 添加选择
      setSelectedStones([...selectedStones, { pile, index }]);
    }
  };

  // 验证选择是否合法
  const validateSelection = () => {
    if (selectedStones.length === 0) {
      return { valid: false, message: "请选择至少一颗石头" };
    }

    const aStones = selectedStones.filter((s) => s.pile === "A");
    const bStones = selectedStones.filter((s) => s.pile === "B");

    // 检查是否只从一堆选择
    if (aStones.length > 0 && bStones.length === 0) {
      // 只从A堆取，无需检查连续性
      return { valid: true, message: "" };
    }

    if (bStones.length > 0 && aStones.length === 0) {
      // 只从B堆取，无需检查连续性
      return { valid: true, message: "" };
    }

    // 从两堆都有选择，检查数量是否相等
    if (aStones.length !== bStones.length) {
      return { valid: false, message: "从两堆取石头时，数量必须相等！" };
    }

    return { valid: true, message: "" };
  };

  // 确认玩家移动
  const confirmPlayerMove = () => {
    const validation = validateSelection();
    if (!validation.valid) {
      showErrorMessage(validation.message);
      return;
    }

    const aStones = selectedStones.filter((s) => s.pile === "A");
    const bStones = selectedStones.filter((s) => s.pile === "B");

    let move;
    if (aStones.length > 0 && bStones.length === 0) {
      move = { type: "single", pile: "A", amount: aStones.length };
    } else if (bStones.length > 0 && aStones.length === 0) {
      move = { type: "single", pile: "B", amount: bStones.length };
    } else {
      move = { type: "both", amount: aStones.length };
    }

    executeMove(move);
    setAiMoveMessage(""); // 清除AI移动消息
  };

  // 电脑回合
  useEffect(() => {
    if (
      gameStatus === "playing" &&
      (gameMode === "ai-easy" || gameMode === "ai-hard") &&
      currentPlayer === 2
    ) {
      const timer = setTimeout(() => {
        makeAIMove();
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [currentPlayer, gameStatus, gameMode]);

  // 重新开始游戏
  const resetGame = () => {
    // 确保使用有效的配置值
    const validPileA = Math.max(1, parseInt(customPileA) || 10);
    const validPileB = Math.max(1, parseInt(customPileB) || 20);
    
    setPileA(validPileA);
    setPileB(validPileB);
    setCurrentPlayer(
      gameMode === "pvp" ? 1 : playerFirst ? 1 : 2
    );
    setGameStatus("playing");
    setWinner(null);
    setSelectedStones([]);
    setAiMoveMessage("");
    setFlashingStones([]);
  };

  // 应用自定义设置
  const applySettings = () => {
    // 确保所有值都是有效的
    const validPileA = Math.max(1, parseInt(customPileA) || 10);
    const validPileB = Math.max(1, parseInt(customPileB) || 20);
    
    setCustomPileA(validPileA);
    setCustomPileB(validPileB);
    setPileA(validPileA);
    setPileB(validPileB);
    setShowSettings(false);
    resetGame();
  };

  // 更新A堆数量
  const updatePileACount = (value) => {
    setCustomPileA(value); // 允许输入过程中的任何值
  };

  // 更新B堆数量
  const updatePileBCount = (value) => {
    setCustomPileB(value); // 允许输入过程中的任何值
  };

  // 验证并修正输入值（失去焦点时）
  const validatePileACount = () => {
    const validValue = Math.max(1, parseInt(customPileA) || 10);
    setCustomPileA(validValue);
  };

  const validatePileBCount = () => {
    const validValue = Math.max(1, parseInt(customPileB) || 20);
    setCustomPileB(validValue);
  };

  // 渲染石头堆
  const renderStones = (count, label) => {
    const stones = [];
    const maxPerRow = 10; // 每行最多10个
    const rows = Math.ceil(count / maxPerRow);

    for (let row = 0; row < rows; row++) {
      const stonesInRow = Math.min(maxPerRow, count - row * maxPerRow);
      const rowStones = [];

      for (let i = 0; i < stonesInRow; i++) {
        const stoneIndex = row * maxPerRow + i;
        const isSelected = selectedStones.some(
          (s) => s.pile === label && s.index === stoneIndex
        );
        const isFlashing = flashingStones.some(
          (s) => s.pile === label && s.index === stoneIndex
        );

        rowStones.push(
          <div
            key={`${label}-${stoneIndex}`}
            className={`w-5 h-5 rounded-full m-1 cursor-pointer transition-all transform hover:scale-110 ${
              isSelected ? "ring-2 ring-blue-500 ring-offset-1" : ""
            } ${isFlashing ? "animate-pulse" : ""}`}
            style={{
              background: isSelected
                ? "linear-gradient(45deg, #3b82f6, #1d4ed8)"
                : isFlashing
                ? "linear-gradient(45deg, #ef4444, #dc2626)"
                : label === "A"
                ? "linear-gradient(45deg, #d97706, #92400e)"
                : "linear-gradient(45deg, #7c3aed, #5b21b6)",
            }}
            onClick={() => handleStoneClick(label, stoneIndex)}
          ></div>
        );
      }

      stones.push(
        <div key={`row-${row}`} className="flex justify-center">
          {rowStones}
        </div>
      );
    }

    return stones;
  };

  const isCurrentlyLosingPosition = isLosingPosition(pileA, pileB);

  return (
    <div className="max-w-5xl mx-auto p-6 bg-gradient-to-b from-blue-100 to-blue-200 min-h-screen">
      <style jsx>{`
        @keyframes flash {
          0%,
          100% {
            opacity: 1;
          }
          50% {
            opacity: 0.3;
          }
        }
        @keyframes slideIn {
          from {
            transform: translateY(10px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        @keyframes slideOut {
          from {
            transform: translateY(0);
            opacity: 1;
          }
          to {
            transform: translateY(-10px);
            opacity: 0;
          }
        }
        .slide-in {
          animation: slideIn 0.3s ease-out;
        }
        .slide-out {
          animation: slideOut 0.3s ease-in;
        }
      `}</style>

      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-blue-800">
            🏛️ Wythoff's Game 威索夫游戏
          </h1>
          <button
            onClick={() => setShowSettings(true)}
            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 flex items-center gap-2"
          >
            ⚙️ 游戏设置
          </button>
        </div>

        {/* 游戏模式选择 */}
        <div className="mb-6 text-center">
          <div className="flex justify-center gap-4">
            <button
              onClick={() => {
                setGameMode("pvp");
                resetGame();
              }}
              className={`px-4 py-2 rounded ${
                gameMode === "pvp" ? "bg-blue-500 text-white" : "bg-gray-200"
              }`}
            >
              人与人对战
            </button>
            <button
              onClick={() => {
                setGameMode("ai-easy");
                resetGame();
              }}
              className={`px-4 py-2 rounded ${
                gameMode === "ai-easy"
                  ? "bg-blue-500 text-white"
                  : "bg-gray-200"
              }`}
            >
              人机对战(普通)
            </button>
            <button
              onClick={() => {
                setGameMode("ai-hard");
                resetGame();
              }}
              className={`px-4 py-2 rounded ${
                gameMode === "ai-hard"
                  ? "bg-blue-500 text-white"
                  : "bg-gray-200"
              }`}
            >
              人机对战(高级)
            </button>
          </div>
        </div>

        {/* AI移动消息 */}
        {aiMoveMessage && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-blue-800 font-medium">💻 {aiMoveMessage}</p>
          </div>
        )}

        {/* 游戏状态 */}
        <div className="text-center mb-6">
          {gameStatus === "playing" ? (
            <div>
              <p className="text-xl mb-2">
                轮到:{" "}
                <span className="font-bold text-blue-600">
                  {gameMode === "pvp"
                    ? `玩家 ${currentPlayer}`
                    : currentPlayer === 1
                    ? "玩家"
                    : "电脑"}
                </span>
              </p>
              {(gameMode === "ai-easy" || gameMode === "ai-hard") &&
                currentPlayer === 2 && (
                  <p className="text-gray-600">电脑思考中...</p>
                )}
              {selectedStones.length > 0 && (
                <p className="text-sm text-gray-600 mt-1">
                  已选择 {selectedStones.length} 颗石头
                  {selectedStones.filter((s) => s.pile === "A").length > 0 &&
                    selectedStones.filter((s) => s.pile === "B").length > 0 &&
                    " (两堆各" +
                      selectedStones.filter((s) => s.pile === "A").length +
                      "颗)"}
                </p>
              )}
            </div>
          ) : (
            <div className="text-2xl font-bold">
              🎉{" "}
              {gameMode === "pvp"
                ? `玩家 ${winner}`
                : winner === 1
                ? "玩家"
                : "电脑"}{" "}
              获胜！
            </div>
          )}
        </div>

        {/* 游戏区域 */}
        <div className="grid grid-cols-2 gap-8 mb-6">
          {/* A堆 */}
          <div className="text-center">
            <h3 className="text-xl font-bold mb-4 text-orange-600">
              A堆 ({pileA}颗)
            </h3>
            <div className="bg-orange-50 rounded-lg p-4 min-h-[200px] flex flex-col justify-center">
              {pileA > 0 ? (
                renderStones(pileA, "A")
              ) : (
                <div className="text-gray-400 italic">空堆</div>
              )}
            </div>
          </div>

          {/* B堆 */}
          <div className="text-center">
            <h3 className="text-xl font-bold mb-4 text-purple-600">
              B堆 ({pileB}颗)
            </h3>
            <div className="bg-purple-50 rounded-lg p-4 min-h-[200px] flex flex-col justify-center">
              {pileB > 0 ? (
                renderStones(pileB, "B")
              ) : (
                <div className="text-gray-400 italic">空堆</div>
              )}
            </div>
          </div>
        </div>

        {/* 错误提示 - 显示在游戏区域下方 */}
        {showError && (
          <div className="mb-4 flex justify-center">
            <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-3 rounded-lg shadow-lg flex items-center gap-2 slide-in max-w-md">
              <span className="text-xl">⚠️</span>
              <span className="text-sm font-medium">{errorMessage}</span>
            </div>
          </div>
        )}

        {/* 操作按钮 */}
        {gameStatus === "playing" &&
          selectedStones.length > 0 &&
          (gameMode === "pvp" || (gameMode !== "pvp" && currentPlayer === 1)) &&
          flashingStones.length === 0 && (
            <div className="flex justify-center gap-4 mb-4">
              <button
                onClick={confirmPlayerMove}
                className="px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 font-bold"
              >
                确认拿取 ({selectedStones.length} 颗)
              </button>
              <button
                onClick={() => setSelectedStones([])}
                className="px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
              >
                取消选择
              </button>
            </div>
          )}

        {/* 重新开始 */}
        <div className="text-center mb-6 mt-8">
          <button
            onClick={resetGame}
            className="px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 font-bold mr-4"
          >
            重新开始
          </button>
        </div>

        {/* 游戏规则 */}
        <div className="p-4 bg-gray-50 rounded-lg">
          <h3 className="font-bold mb-2">游戏规则:</h3>
          <ul className="text-sm space-y-1">
            <li>• 点击石头来选择，选中的石头会变蓝色</li>
            <li>• 可以从一堆中取任意数量的石头</li>
            <li>• 或者可以同时从两堆取相同数量的石头</li>
            <li>• 谁取走最后一颗石头就算获胜</li>
          </ul>
        </div>
      </div>

      {/* 设置弹窗 */}
      {showSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h2 className="text-2xl font-bold mb-4">游戏设置</h2>
            
            {/* 先手设置 */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                人机对战先手设置
              </label>
              <div className="flex gap-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    checked={playerFirst}
                    onChange={() => setPlayerFirst(true)}
                    className="mr-2"
                  />
                  玩家先手
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    checked={!playerFirst}
                    onChange={() => setPlayerFirst(false)}
                    className="mr-2"
                  />
                  电脑先手
                </label>
              </div>
            </div>

            {/* 自定义堆配置 */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                自定义游戏配置
              </label>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="w-16 text-sm font-medium text-orange-600">A堆:</span>
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={customPileA}
                    onChange={(e) => updatePileACount(e.target.value)}
                    onBlur={validatePileACount}
                    placeholder="输入数量"
                    className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm focus:border-blue-500 focus:outline-none"
                  />
                  <span className="text-sm text-gray-500">颗石头</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-16 text-sm font-medium text-purple-600">B堆:</span>
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={customPileB}
                    onChange={(e) => updatePileBCount(e.target.value)}
                    onBlur={validatePileBCount}
                    placeholder="输入数量"
                    className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm focus:border-blue-500 focus:outline-none"
                  />
                  <span className="text-sm text-gray-500">颗石头</span>
                </div>
              </div>
            </div>

            {/* 游戏提示 */}
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-xs text-blue-800">
                💡 提示：威索夫游戏有特殊的必败态规律，与黄金比例相关！
              </p>
            </div>

            <div className="flex justify-end gap-4">
              <button
                onClick={() => setShowSettings(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
              >
                取消
              </button>
              <button
                onClick={applySettings}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                应用设置
              </button>
            </div>
          </div>
        </div>
      )}

      <footer className="mt-8 w-full text-center text-sm text-gray-500">
        © {new Date().getFullYear()} 孙维刚教育研究院-陈硕老师 保留所有权利。
      </footer>
    </div>
  );
};

export default WythoffGame;
